/** Request fields, photo picker and quick customer creation. */
import { useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type {
  CategoryNodeResponse,
  CustomerDuplicate,
  CustomerResponse,
  PricingPreference,
  Priority,
  RequestResponse,
  SchedulingPreference,
} from "@offertli/shared";
import { Button } from "../../components/ui/Button.js";
import { InputText } from "@primereact/ui/inputtext";
import { Textarea } from "@primereact/ui/textarea";
import { apiGet, apiPost, getCsrfToken } from "../../lib/api.js";
import { usePermissions } from "../../lib/auth.js";
import { prepareUpload, uploadWithProgress } from "../../lib/images.js";
import { Field } from "../../components/ui/Field.js";
import { cx } from "../../components/ui/cx.js";
import { CheckboxField, Modal } from "../../components/ui/controls.js";
import { MutationError } from "../shared.js";
import {
  CustomerForm,
  createPayload,
  customerDraftValid,
  emptyCustomer,
} from "../customers/CustomerForm.js";
import { AIRequestCapture, type AIRequestChanges } from "./AIRequestCapture.js";
import {
  Chips,
  Step,
  useCategories,
  useCategoryName,
} from "./request-common.js";

export interface RequestDraft {
  categoryId: string;
  subcategoryId: string;
  shortDescription: string;
  description: string;
  siteChoice: string; // "billing" | site id | "other"
  siteLine1: string;
  sitePostalCode: string;
  siteCity: string;
  pricingPreference: PricingPreference;
  fixedAmount: string;
  fixedAmountTaxIncluded: boolean;
  schedulingPreference: SchedulingPreference;
  requestedDate: string;
  requestedTimeStart: string;
  requestedTimeEnd: string;
  timeFlexible: boolean;
  priority: Priority;
  assignedUserIds: string[];
}

export function emptyDraft(): RequestDraft {
  return {
    categoryId: "",
    subcategoryId: "",
    shortDescription: "",
    description: "",
    siteChoice: "billing",
    siteLine1: "",
    sitePostalCode: "",
    siteCity: "",
    pricingPreference: "offer_required",
    fixedAmount: "",
    fixedAmountTaxIncluded: true,
    schedulingPreference: "asap",
    requestedDate: "",
    requestedTimeStart: "",
    requestedTimeEnd: "",
    timeFlexible: false,
    priority: "normal",
    assignedUserIds: [],
  };
}

export function draftFromRequest(
  request: RequestResponse,
  categories: CategoryNodeResponse[] | undefined,
): RequestDraft {
  const parent = categories?.find(
    (category) =>
      category.id === request.categoryId ||
      category.children.some((child) => child.id === request.categoryId),
  );
  const isChild = parent && parent.id !== request.categoryId;
  return {
    categoryId: parent?.id ?? "",
    subcategoryId: isChild ? (request.categoryId ?? "") : "",
    shortDescription: request.shortDescription,
    description: request.description ?? "",
    siteChoice: request.siteAddressId
      ? request.siteAddressId
      : request.siteAddress
        ? "other"
        : "billing",
    siteLine1: request.siteAddress?.line1 ?? "",
    sitePostalCode: request.siteAddress?.postalCode ?? "",
    siteCity: request.siteAddress?.city ?? "",
    pricingPreference: request.pricingPreference as PricingPreference,
    fixedAmount: request.fixedAmount ?? "",
    fixedAmountTaxIncluded: request.fixedAmountTaxIncluded,
    schedulingPreference: request.schedulingPreference as SchedulingPreference,
    requestedDate: request.requestedDate ?? "",
    requestedTimeStart: request.requestedTimeStart ?? "",
    requestedTimeEnd: request.requestedTimeEnd ?? "",
    timeFlexible: request.timeFlexible,
    priority: request.priority,
    assignedUserIds: request.assignedUsers.map((user) => user.id),
  };
}

export function requestPayload(
  draft: RequestDraft,
  customer: CustomerResponse,
  canFinance: boolean,
  canAssign: boolean,
) {
  const site =
    draft.siteChoice === "billing"
      ? { siteAddressId: null, siteAddress: customer.billingAddress ?? null }
      : draft.siteChoice === "other"
        ? {
            siteAddressId: null,
            siteAddress: draft.siteLine1.trim()
              ? {
                  line1: draft.siteLine1.trim(),
                  postalCode: draft.sitePostalCode.trim(),
                  city: draft.siteCity.trim(),
                  country: "CH",
                }
              : null,
          }
        : { siteAddressId: draft.siteChoice, siteAddress: null };
  const dated = draft.schedulingPreference === "desired_date";
  return {
    customerId: customer.id,
    categoryId: draft.subcategoryId || draft.categoryId || null,
    shortDescription: draft.shortDescription.trim(),
    description: draft.description.trim() || null,
    ...site,
    ...(canFinance
      ? {
          pricingPreference: draft.pricingPreference,
          ...(draft.pricingPreference === "fixed_price"
            ? {
                fixedAmount: draft.fixedAmount.replace(",", "."),
                fixedAmountTaxIncluded: draft.fixedAmountTaxIncluded,
              }
            : {}),
        }
      : {}),
    schedulingPreference: draft.schedulingPreference,
    requestedDate: dated ? draft.requestedDate || null : null,
    requestedTimeStart:
      dated && draft.requestedTimeStart ? draft.requestedTimeStart : null,
    requestedTimeEnd:
      dated && draft.requestedTimeEnd ? draft.requestedTimeEnd : null,
    timeFlexible: draft.timeFlexible,
    priority: draft.priority,
    ...(canAssign ? { assignedUserIds: draft.assignedUserIds } : {}),
  };
}

export function draftValid(draft: RequestDraft, canFinance: boolean) {
  if (!draft.shortDescription.trim()) return false;
  if (draft.schedulingPreference === "desired_date" && !draft.requestedDate)
    return false;
  if (
    draft.requestedTimeStart &&
    draft.requestedTimeEnd &&
    draft.requestedTimeEnd <= draft.requestedTimeStart
  )
    return false;
  if (
    canFinance &&
    draft.pricingPreference === "fixed_price" &&
    !/^\d+([.,]\d{1,2})?$/.test(draft.fixedAmount.trim())
  )
    return false;
  if (
    draft.siteChoice === "other" &&
    draft.siteLine1.trim() &&
    (!draft.sitePostalCode.trim() || !draft.siteCity.trim())
  )
    return false;
  return true;
}

export function RequestFields({
  draft,
  onChange,
  customer,
  firstStep,
}: {
  draft: RequestDraft;
  onChange: (draft: RequestDraft) => void;
  customer: CustomerResponse;
  firstStep: number;
}) {
  const { t } = useTranslation();
  const { has } = usePermissions();
  const categories = useCategories();
  const categoryName = useCategoryName();
  const canFinance = has("finance.view");
  const canAssign = has("requests.assign");
  const assignees = useQuery({
    queryKey: ["requests", "assignees"],
    queryFn: () =>
      apiGet<{
        data: Array<{ id: string; firstName: string; lastName: string }>;
      }>("/requests/assignees").then((response) => response.data),
    enabled: canAssign,
  });
  const set = <K extends keyof RequestDraft>(key: K, value: RequestDraft[K]) =>
    onChange({ ...draft, [key]: value });
  const activeCategories = (categories.data ?? []).filter((c) => c.active);
  const parent = activeCategories.find((c) => c.id === draft.categoryId);
  const children = (parent?.children ?? []).filter((c) => c.active);

  const aiLabels = useMemo(
    () => ({
      categoryId: Object.fromEntries(
        activeCategories.map((c) => [c.id, categoryName(c)]),
      ),
      subcategoryId: Object.fromEntries(
        activeCategories.flatMap((c) =>
          c.children.map((child) => [child.id, categoryName(child)]),
        ),
      ),
      pricingPreference: {
        offer_required: t("request.pricing.offer_required"),
        fixed_price: t("request.pricing.fixed_price"),
        time_and_material: t("request.pricing.time_and_material"),
      },
      schedulingPreference: {
        asap: t("request.asap"),
        desired_date: t("request.desiredDate"),
      },
      priority: {
        low: t("status.low"),
        normal: t("status.normal"),
        high: t("status.high"),
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [categories.data, t],
  );
  const applyAI = (changes: AIRequestChanges) => {
    const next = { ...draft };
    for (const [key, value] of Object.entries(changes)) {
      if (value === undefined) continue;
      if (key === "fixedAmount" && !canFinance) continue;
      if (key === "pricingPreference" && !canFinance) continue;
      (next as unknown as Record<string, string>)[
        key === "subcategoryId" ? "subcategoryId" : key
      ] = value;
    }
    onChange(next);
  };

  const siteOptions = [
    {
      value: "billing",
      label: customer.billingAddress
        ? `${t("request.siteBilling")} · ${customer.billingAddress.line1}, ${customer.billingAddress.city}`
        : t("request.siteBilling"),
    },
    ...customer.sites.map((site) => ({
      value: site.id,
      label: `${site.label ? `${site.label} · ` : ""}${site.line1}, ${site.city}`,
    })),
    { value: "other", label: t("request.siteOther") },
  ];

  return (
    <>
      <Step number={firstStep} title={t("request.stepWhere")}>
        <div role="radiogroup" className="space-y-2">
          {siteOptions.map((option) => (
            <label
              key={option.value}
              className={cx(
                "flex min-h-11 cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-sm",
                draft.siteChoice === option.value
                  ? "border-brand bg-brand-soft"
                  : "border-app-border",
              )}
            >
              <input
                type="radio"
                name="site"
                value={option.value}
                checked={draft.siteChoice === option.value}
                onChange={() => set("siteChoice", option.value)}
                className="accent-[var(--color-brand)]"
              />
              {option.label}
            </label>
          ))}
        </div>
        {draft.siteChoice === "other" ? (
          <div className="grid gap-3 sm:grid-cols-6">
            <Field label={t("common.address")} className="sm:col-span-6">
              <InputText
                value={draft.siteLine1}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  set("siteLine1", event.target.value)
                }
                autoComplete="address-line1"
              />
            </Field>
            <Field label={t("common.postalCode")} className="sm:col-span-2">
              <InputText
                value={draft.sitePostalCode}
                inputMode="numeric"
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  set("sitePostalCode", event.target.value)
                }
              />
            </Field>
            <Field label={t("common.city")} className="sm:col-span-4">
              <InputText
                value={draft.siteCity}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  set("siteCity", event.target.value)
                }
              />
            </Field>
          </div>
        ) : null}
      </Step>

      <Step number={firstStep + 1} title={t("request.stepWhat")}>
        <AIRequestCapture
          valueLabels={aiLabels}
          currentFields={{
            shortDescription: draft.shortDescription,
            description: draft.description,
            categoryId: draft.categoryId,
            subcategoryId: draft.subcategoryId,
            pricingPreference: draft.pricingPreference,
            fixedAmount: draft.fixedAmount,
            schedulingPreference: draft.schedulingPreference,
            requestedDate: draft.requestedDate,
            requestedTimeStart: draft.requestedTimeStart,
            requestedTimeEnd: draft.requestedTimeEnd,
            priority: draft.priority,
          }}
          onApply={applyAI}
        />
        {activeCategories.length ? (
          <Field label={t("request.category")}>
            <Chips
              label={t("request.category")}
              value={draft.categoryId}
              onChange={(categoryId) =>
                onChange({ ...draft, categoryId, subcategoryId: "" })
              }
              options={activeCategories.map((category) => ({
                value: category.id,
                label: categoryName(category),
              }))}
            />
          </Field>
        ) : null}
        {children.length ? (
          <Field label={t("request.subcategory")}>
            <Chips
              label={t("request.subcategory")}
              value={draft.subcategoryId}
              onChange={(subcategoryId) => set("subcategoryId", subcategoryId)}
              options={children.map((category) => ({
                value: category.id,
                label: categoryName(category),
              }))}
            />
          </Field>
        ) : null}
        <Field
          label={t("request.shortDescription")}
          required
          htmlFor="req-short"
        >
          <InputText
            id="req-short"
            value={draft.shortDescription}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              set("shortDescription", event.target.value)
            }
            placeholder={t("request.shortPlaceholder")}
            maxLength={180}
          />
        </Field>
        <Field label={t("request.details")} htmlFor="req-details">
          <Textarea
            id="req-details"
            value={draft.description}
            rows={4}
            autoResize
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
              set("description", event.target.value)
            }
            placeholder={t("request.detailsPlaceholder")}
          />
        </Field>
      </Step>

      <Step number={firstStep + 2} title={t("request.stepWhen")}>
        <Chips
          label={t("request.when")}
          value={draft.schedulingPreference}
          onChange={(value) =>
            set(
              "schedulingPreference",
              (value || "asap") as SchedulingPreference,
            )
          }
          options={[
            { value: "asap", label: t("request.asap") },
            { value: "desired_date", label: t("request.desiredDate") },
          ]}
        />
        {draft.schedulingPreference === "desired_date" ? (
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label={t("common.date")} required>
              <InputText
                type="date"
                value={draft.requestedDate}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  set("requestedDate", event.target.value)
                }
              />
            </Field>
            <Field label={t("request.timeFrom")}>
              <InputText
                type="time"
                value={draft.requestedTimeStart}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  set("requestedTimeStart", event.target.value)
                }
              />
            </Field>
            <Field label={t("request.timeTo")}>
              <InputText
                type="time"
                value={draft.requestedTimeEnd}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  set("requestedTimeEnd", event.target.value)
                }
              />
            </Field>
            <div className="sm:col-span-3">
              <CheckboxField
                id="req-flexible"
                checked={draft.timeFlexible}
                onChange={(value) => set("timeFlexible", value)}
                label={t("request.timeFlexible")}
              />
            </div>
          </div>
        ) : null}
        <Field label={t("request.priority")}>
          <Chips
            label={t("request.priority")}
            value={draft.priority}
            onChange={(value) =>
              set("priority", (value || "normal") as Priority)
            }
            options={(["low", "normal", "high"] as const).map((value) => ({
              value,
              label: t(`status.${value}`),
            }))}
          />
        </Field>
        {canFinance ? (
          <>
            <Field label={t("request.priceExpectation")}>
              <Chips
                label={t("request.priceExpectation")}
                value={draft.pricingPreference}
                onChange={(value) =>
                  set(
                    "pricingPreference",
                    (value || "offer_required") as PricingPreference,
                  )
                }
                options={(
                  [
                    "offer_required",
                    "fixed_price",
                    "time_and_material",
                  ] as const
                ).map((value) => ({
                  value,
                  label: t(`request.pricing.${value}`),
                }))}
              />
            </Field>
            {draft.pricingPreference === "fixed_price" ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label={t("request.fixedAmount")} required>
                  <InputText
                    inputMode="decimal"
                    value={draft.fixedAmount}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      set("fixedAmount", event.target.value)
                    }
                    placeholder="0.00"
                  />
                </Field>
                <div className="self-end pb-2">
                  <CheckboxField
                    id="req-tax-included"
                    checked={draft.fixedAmountTaxIncluded}
                    onChange={(value) => set("fixedAmountTaxIncluded", value)}
                    label={t("request.taxIncluded")}
                  />
                </div>
              </div>
            ) : null}
          </>
        ) : null}
        {canAssign && assignees.data?.length ? (
          <Field label={t("request.assignees")}>
            <div className="flex flex-wrap gap-2">
              {assignees.data.map((user) => {
                const selected = draft.assignedUserIds.includes(user.id);
                return (
                  <button
                    key={user.id}
                    type="button"
                    aria-pressed={selected}
                    onClick={() =>
                      set(
                        "assignedUserIds",
                        selected
                          ? draft.assignedUserIds.filter((id) => id !== user.id)
                          : [...draft.assignedUserIds, user.id],
                      )
                    }
                    className={cx(
                      "min-h-11 rounded-full border px-4 py-2 text-sm",
                      selected
                        ? "border-brand bg-brand-soft text-app-text"
                        : "border-app-border text-app-muted",
                    )}
                  >
                    {selected ? "✓ " : ""}
                    {user.firstName} {user.lastName}
                  </button>
                );
              })}
            </div>
          </Field>
        ) : null}
      </Step>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Photos                                                              */
/* ------------------------------------------------------------------ */

export interface PendingPhoto {
  key: string;
  file: File;
  preview: string | null;
  caption: string;
  progress: number;
  error?: string;
}

export function PhotoPicker({
  photos,
  onChange,
}: {
  photos: PendingPhoto[];
  onChange: (photos: PendingPhoto[]) => void;
}) {
  const { t } = useTranslation();
  const camera = useRef<HTMLInputElement>(null);
  const gallery = useRef<HTMLInputElement>(null);
  const add = async (files: FileList | null) => {
    if (!files) return;
    const prepared = await Promise.all(
      [...files].map(async (file) => {
        const ready = await prepareUpload(file);
        return {
          key: `${Date.now()}-${Math.random()}`,
          file: ready,
          preview: ready.type.startsWith("image/")
            ? URL.createObjectURL(ready)
            : null,
          caption: "",
          progress: 0,
        };
      }),
    );
    onChange([...photos, ...prepared]);
  };
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <Button
          type="button"
          variant="outlined"
          icon="pi pi-camera"
          className="min-h-12"
          onClick={() => camera.current?.click()}
        >
          {t("request.takePhoto")}
        </Button>
        <Button
          type="button"
          variant="outlined"
          severity="secondary"
          icon="pi pi-images"
          className="min-h-12"
          onClick={() => gallery.current?.click()}
        >
          {t("request.choosePhotos")}
        </Button>
        <input
          ref={camera}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(event) => {
            void add(event.target.files);
            event.target.value = "";
          }}
        />
        <input
          ref={gallery}
          type="file"
          accept="image/*,application/pdf"
          multiple
          className="hidden"
          onChange={(event) => {
            void add(event.target.files);
            event.target.value = "";
          }}
        />
      </div>
      {photos.length ? (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {photos.map((photo) => (
            <li
              key={photo.key}
              className="overflow-hidden rounded-lg border border-app-border"
            >
              <div className="relative aspect-square bg-app-background">
                {photo.preview ? (
                  <img
                    src={photo.preview}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-app-muted">
                    <i className="pi pi-file-pdf text-3xl" aria-hidden="true" />
                  </div>
                )}
                <button
                  type="button"
                  aria-label={t("common.remove")}
                  className="absolute right-1 top-1 flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white"
                  onClick={() => {
                    if (photo.preview) URL.revokeObjectURL(photo.preview);
                    onChange(photos.filter((other) => other.key !== photo.key));
                  }}
                >
                  <i className="pi pi-times" aria-hidden="true" />
                </button>
                {photo.progress > 0 && photo.progress < 1 ? (
                  <div className="absolute inset-x-0 bottom-0 h-1.5 bg-black/20">
                    <div
                      className="h-full bg-brand"
                      style={{ width: `${Math.round(photo.progress * 100)}%` }}
                    />
                  </div>
                ) : null}
              </div>
              <InputText
                value={photo.caption}
                placeholder={t("request.caption")}
                aria-label={t("request.caption")}
                className="w-full rounded-none border-0 border-t text-sm"
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  onChange(
                    photos.map((other) =>
                      other.key === photo.key
                        ? { ...other, caption: event.target.value }
                        : other,
                    ),
                  )
                }
              />
              {photo.error ? (
                <p className="px-2 py-1 text-xs text-danger">{photo.error}</p>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-app-muted">{t("request.photosHint")}</p>
      )}
    </div>
  );
}

export function QuickCustomerDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (customer: CustomerResponse) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(emptyCustomer);
  useEffect(() => {
    if (open) setDraft(emptyCustomer());
  }, [open]);
  const create = useMutation({
    mutationFn: () =>
      apiPost<{ data: CustomerResponse; duplicates: CustomerDuplicate[] }>(
        "/customers",
        createPayload(draft),
      ),
    onSuccess: (response) => onCreated(response.data),
  });
  return (
    <Modal
      open={open}
      onClose={onClose}
      wide
      title={t("customer.new")}
      footer={
        <>
          <Button variant="text" severity="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            loading={create.isPending || undefined}
            disabled={!customerDraftValid(draft)}
            onClick={() => create.mutate()}
          >
            {t("customer.createAndUse")}
          </Button>
        </>
      }
    >
      <CustomerForm value={draft} onChange={setDraft} compact />
      <MutationError error={create.error} />
    </Modal>
  );
}

export async function uploadPhotos(
  requestId: string,
  photos: PendingPhoto[],
  onProgress: (key: string, progress: number, error?: string) => void,
): Promise<number> {
  let failed = 0;
  for (const photo of photos) {
    const form = new FormData();
    if (photo.caption.trim()) form.append("caption", photo.caption.trim());
    form.append("file", photo.file, photo.file.name);
    try {
      await uploadWithProgress(
        `/api/v1/requests/${requestId}/attachments`,
        form,
        getCsrfToken(),
        (fraction) => onProgress(photo.key, fraction),
      );
      onProgress(photo.key, 1);
    } catch (error) {
      failed += 1;
      onProgress(photo.key, 0, error instanceof Error ? error.message : "");
    }
  }
  return failed;
}

/* ------------------------------------------------------------------ */
/* New request                                                         */
/* ------------------------------------------------------------------ */
